import { BookOpen, ExternalLink, Server } from 'lucide-react';
import { useAuth } from '../../../lib/auth-context.js';
import { useVersion } from '../../common/hooks.js';

/**
 * Support documentation. External-doc links + the environment
 * fingerprint customers need when escalating to support. Dark-themed
 * to match the rest of the SPA.
 */
export function SupportDocumentationPage(): JSX.Element {
  const { session } = useAuth();
  const { data: version } = useVersion();
  return (
    <div className="relative z-10 flex h-full flex-col">
      <header className="px-6 pb-2 pt-6">
        <h1 className="text-2xl font-light text-foreground">Documentation</h1>
        <p className="text-sm text-foreground/60">
          Reference material + your environment fingerprint.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-3 px-6 py-3 lg:grid-cols-2">
        <section className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-4">
          <header className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground/80">
            <BookOpen className="h-4 w-4" /> Quick links
          </header>
          <ul className="space-y-2 text-sm">
            <li>
              <DocLink href="https://help.workfront.com/" label="Workfront help" />
            </li>
            <li>
              <DocLink
                href="https://experience.adobe.com/#/@adobe/sname:prod/aem/"
                label="Adobe Experience Cloud"
              />
            </li>
            <li>
              <DocLink
                href="https://experience.adobe.com/#/@adobe/sname:prod/workfront-fusion"
                label="Workfront Fusion"
              />
            </li>
            <li>
              <DocLink href="https://workfocus.io/fcoe/docs/introduction" label="WorkFocus docs" />
            </li>
          </ul>
        </section>

        <section className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-4">
          <header className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground/80">
            <Server className="h-4 w-4" /> Environment fingerprint
          </header>
          <dl className="grid grid-cols-[6rem_1fr] gap-y-2 text-sm">
            <Row label="License" value={session?.licenseId ?? '—'} mono />
            <Row label="User" value={session?.email ?? session?.workfrontUserId ?? '—'} />
            <Row label="Env" value={version?.env ?? '—'} mono />
            <Row label="Commit" value={version?.commitSha ?? '—'} mono />
          </dl>
          <p className="mt-3 text-xs text-foreground/40">
            Include this fingerprint in support requests so engineering can locate the deployment
            you're on.
          </p>
        </section>
      </div>
    </div>
  );
}

function DocLink({ href, label }: { href: string; label: string }): JSX.Element {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 text-blue-700 dark:text-blue-300 underline transition-colors hover:text-blue-800 dark:hover:text-blue-200"
    >
      {label}
      <ExternalLink className="h-3 w-3 text-foreground/40" />
    </a>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <>
      <dt className="text-xs text-foreground/50">{label}</dt>
      <dd
        className={
          mono
            ? 'truncate font-mono text-xs text-foreground/85'
            : 'truncate text-sm text-foreground/85'
        }
      >
        {value}
      </dd>
    </>
  );
}
